import { UIDateElement } from './ui-dateelement';
import { isKeyPressed, keyCodes } from '../../global/keyboard';
import {
    dispatchNativeEvent,
    updateElement,
    setNativeInputValue,
} from '../../global/ui-helpers';
import { Digest } from '../../global/digest';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import { Child } from '../../decorators/child.decorator';
import type { AttributeValue } from '../ui-element.type';
import type { NullOr } from '../../global/utils.type';
import styles from './ui-timepicker.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIDateElement}
 * @alias UITimePicker
 * @element ui-timepicker
 * @implements {CustomFormComponent}
 * @classdesc Represents a class for <code>ui-timepicker</code> element.
 * Input element which allows to pick time.
 * @fires event:change
 * @property {string} [controlId] {@attr control-id} Control (input element) id. Deprecated! Instead of using control-id, please set id directly on the native input element instead. If no id is set, a random id is generated.
 * @property {HTMLInputElement} control Shortcut to control/input element.
 * @slot
 * @example
 * <ui-timepicker>
 *   <input id="custom-timepicker-id" name="timeFrom" type="time">
 * </ui-timepicker>
 */
@Component({ tag: 'ui-timepicker', styles })
export class UITimePicker extends UIDateElement {
    @Attribute(String) controlId?: string;

    @Child('.ui-timepicker__input') control: HTMLInputElement = null!;

    /**
     * @type {string[]}
     */
    static get observedAttributes(): Array<string> {
        return ['control-id'];
    }

    private _id?: string;
    private _hasUserSetInputId?: boolean;
    focused: boolean = false;

    get valueType(): string {
        return UIDateElement.VALUE_TYPES.TIME;
    }

    get uid(): string {
        if (!this._id) {
            this._id = Digest.randomId();
        }
        return this._id;
    }

    /**
     * Returns the control id for ui-field (label[for], aria-describedby).
     * Priority: user id on input > control-id > uid.
     * @returns {string}
     */
    getControlIdForField(): string {
        const controlIdAttr: NullOr<string> =
            this.controlId || this.getAttribute('control-id') || null;
        const input: NullOr<HTMLInputElement> =
            this.control ||
            (this.queryChildren('input')[0] as HTMLInputElement) ||
            null;
        if (input) {
            const inputId = input.getAttribute('id')?.trim();
            if (inputId) {
                return inputId;
            }
            if (controlIdAttr) {
                if (input.id !== controlIdAttr) {
                    input.id = controlIdAttr;
                    input.setAttribute('id', controlIdAttr);
                }
                return controlIdAttr;
            }
            const effectiveId = input.id || this.uid;
            if (input.id !== effectiveId) {
                input.id = effectiveId;
                input.setAttribute('id', effectiveId);
            }
            return effectiveId;
        }
        return controlIdAttr || this.uid;
    }

    get value(): NullOr<string> {
        if (!this.control) {
            return null;
        }
        return this.isValidTime(this.control.value) ? this.control.value : null;
    }

    set value(value: NullOr<string>) {
        if (value === this.value) {
            return;
        }
        this.setControlValue(value);
    }

    /**
     * Sets value in string representation to input control.
     * @param {string} value
     */
    setControlValue(value: NullOr<string>): void {
        if (this.focused) {
            return;
        }

        let controlValue = value;
        if (this.control?.type === 'text') {
            controlValue = value?.replace(/[,.-]/g, ':') ?? null;
        }
        if (!this.isValidTime(controlValue)) {
            setNativeInputValue(this.control, '');
            dispatchNativeEvent(this.control, 'input', true);
        } else {
            const res = this.parseTimeValue(controlValue!, this.min, this.max);
            if (this.control.value !== res.value) {
                setNativeInputValue(this.control, res.value);
                dispatchNativeEvent(this.control, 'input', true);
            }
        }
    }

    /**
     * Sets hours and minutes to input control.
     * @param {number} hh
     * @param {number} mm
     */
    setTime(hh: number, mm: number): void {
        const hours = `${hh}`.padStart(2, '0');
        const minutes = `${mm}`.padStart(2, '0');
        const parsed = this.parseTimeValue(
            `${hours}:${minutes}`,
            this.min,
            this.max
        ).value;
        setNativeInputValue(this.control, parsed);
        dispatchNativeEvent(this.control, 'input', true);
    }

    /**
     * Moves user selection over to hours.
     * @private
     */
    private selectHours(): void {
        this.control.setSelectionRange(0, 2);
    }

    /**
     * Moves user selection over to minutes.
     * @private
     */
    private selectMinutes(): void {
        this.control.setSelectionRange(3, 5);
    }

    /**
     * Fires callback when the input value is changed.
     * @param {Event} e
     * @private
     */
    private handleChangeValue(e: Event): void {
        this.setControlValue((e.target as HTMLInputElement).value);
    }

    /**
     * Fires callback when the input get focused.
     * @param {Event} e
     * @private
     */
    private handleFocusInput(e: Event): void {
        this.focused = true;
        if (this.control?.type !== 'text') {
            return;
        }
        if (!this.isValidTime(this.control.value)) {
            setNativeInputValue(this.control, '00:00');
        }
        this.selectHours();
    }

    /**
     * Fires callback when the input get focused.
     * @param {Event} e
     * @private
     */
    private handleBlurInput(e: Event): void {
        this.focused = false;
        this.setControlValue(this.control?.value ?? null);
    }

    /**
     * Fires callback when input's selection is changed.
     * @private
     */
    private handleSelectionChanged(): void {
        if (this.control?.type !== 'text') {
            return;
        }
        if ((this.control.selectionStart ?? 0) < 2) {
            this.selectHours();
        } else {
            this.selectMinutes();
        }
    }

    /**
     * Fires callback when the input get clicked.
     * @param {Event} e
     * @private
     */
    private handleClickInInput(e: Event): void {
        if (this.control?.type !== 'text') {
            return;
        }
        this.handleSelectionChanged();
    }

    /**
     * Fires callback when the input get a key down event.
     * @param {MouseEvent|KeyboardEvent} e
     * @private
     */
    private handleKeydownInInput(e: KeyboardEvent): void {
        if (this.control?.type !== 'text') {
            return;
        }
        let { hours, minutes } = this.parseTimeValue(
            this.control.value,
            this.min,
            this.max
        );
        if (
            isKeyPressed(e, keyCodes.BACKSPACE) ||
            isKeyPressed(e, keyCodes.DEL)
        ) {
            return;
        }
        if (isKeyPressed(e, keyCodes.DOWN)) {
            if ((this.control.selectionStart ?? 0) < 2) {
                hours = hours - 1;
                if (hours < 0) {
                    hours = 24;
                }
                this.setTime(hours, minutes);
                this.selectHours();
            } else {
                minutes = minutes - 1;
                if (minutes < 0) {
                    minutes = 59;
                }
                this.setTime(hours, minutes);
                dispatchNativeEvent(this, 'change');
                this.selectMinutes();
            }
            e.preventDefault();
        } else if (isKeyPressed(e, keyCodes.UP)) {
            if ((this.control.selectionStart ?? 0) < 2) {
                hours = hours + 1;
                if (hours > 24) {
                    hours = 0;
                }
                this.setTime(hours, minutes);
                dispatchNativeEvent(this, 'change');
                this.selectHours();
            } else {
                minutes = minutes + 1;
                if (minutes > 59) {
                    minutes = 0;
                }
                this.setTime(hours, minutes);
                this.selectMinutes();
            }
            e.preventDefault();
        } else if (isKeyPressed(e, keyCodes.LEFT)) {
            this.selectHours();
            e.preventDefault();
        } else if (isKeyPressed(e, keyCodes.RIGHT)) {
            this.selectMinutes();
            e.preventDefault();
        }
    }

    /**
     * Fires callback when the value inside the input changes.
     * @param {InputEvent} e
     * @private
     */
    private handleInputValue(e: InputEvent): void {
        if (this.control?.type !== 'text') {
            return;
        }

        const isCursorAfterHours = this.control.selectionStart === 2;
        if (!isCursorAfterHours) {
            return;
        }

        const [hours] = this.control.value.split(':');
        if (hours.length === 2) {
            this.selectMinutes();
        }
    }

    /**
     * @inheritDoc
     */
    observeAttributes(
        name: string,
        oldValue: AttributeValue,
        newValue: NullOr<string>
    ): void {
        if (name === 'control-id' && this.control && !this._hasUserSetInputId) {
            if (newValue) {
                this.control.setAttribute('id', newValue);
            } else {
                this.control.removeAttribute('id');
            }
        }
    }

    /**
     * @inheritDoc
     */
    getControlElement(): HTMLInputElement | undefined {
        return this.control;
    }

    /**
     * @inheritDoc
     */
    onValuePatched(newValue: unknown): void {
        if (!this.isValidTime(newValue as string)) {
            return;
        }
        setNativeInputValue(this.control, newValue as string);
        dispatchNativeEvent(this.control, 'input', true);
    }

    /**
     * @inheritDoc
     */
    onDisabledStateChanged(newState: boolean): void {
        if (this.control) {
            this.control.disabled = newState;
        }
    }

    /**
     * @inheritDoc
     */
    onReadonlyStateChanged(newState: boolean): void {
        if (this.control) {
            this.control.readOnly = newState;
        }
    }

    /**
     * @inheritDoc
     */
    override render(): void {
        const element = this.queryChildren('input')[0] as
            | HTMLInputElement
            | undefined;
        this.detachChildNodes();

        if (this._hasUserSetInputId === undefined) {
            this._hasUserSetInputId = !!element?.getAttribute('id')?.trim();
        }
        let inputId: NullOr<string> = element?.getAttribute('id') ?? null;
        if (!inputId) {
            inputId =
                this.controlId || this.getAttribute('control-id') || this.uid;
        }

        const type = element?.getAttribute('type') || 'time';
        this.insertElements([
            {
                tagName: 'input',
                element: element ?? null,
                attributes: {
                    type: type,
                    id: inputId,
                },
                classList: {
                    'ui-timepicker__input': true,
                },
            },
        ]);

        if (this.control?.type === 'text') {
            this.control.setAttribute(
                'pattern',
                '^([0-1]?[0-9]|[2][0-3]):([0-5][0-9])|(24:00)$'
            );
            this.control.setAttribute('maxlength', '5');
        }
    }

    /**
     * @inheritDoc
     */
    override hydrate(): void {
        updateElement(this.control, {
            events: {
                change: (e?: Event | CustomEvent<unknown>) =>
                    this.handleChangeValue(e as Event),
                input: (e?: Event | CustomEvent<unknown>) =>
                    this.handleInputValue(e as InputEvent),
                focus: (e?: Event | CustomEvent<unknown>) =>
                    this.handleFocusInput(e as Event),
                blur: (e?: Event | CustomEvent<unknown>) =>
                    this.handleBlurInput(e as Event),
                click: (e?: Event | CustomEvent<unknown>) =>
                    this.handleClickInInput(e as Event),
                keydown: (e?: Event | CustomEvent<unknown>) =>
                    this.handleKeydownInInput(e as KeyboardEvent),
            },
        });
    }
}
